/**
 * The single profile resolver.
 *
 * Maps (resource type + semantic context) -> the expected canonical Unify
 * profile URL. This is the ONE place that decides which profile a resource
 * must carry; stamping, dry-run validation, the validating operation, seed
 * checks, and tests all consume it so they cannot diverge.
 *
 * The resolver is server-derived: it NEVER trusts a client-supplied
 * `meta.profile`. Resources with no Unify profile return `undefined`.
 */
import type { Location, Resource } from '@medplum/fhirtypes';

import { LOCATION_PHYSICAL_TYPE_SYSTEM } from '../constants';
import { UNIFY_PROFILES } from './profile-urls';

/** Location physical-type code for a practice site (building). */
const PHYSICAL_TYPE_BUILDING = 'bu';
/** Location physical-type code for a room. */
const PHYSICAL_TYPE_ROOM = 'ro';

function resolveLocationProfile(resource: Location): string | undefined {
  const code = resource.physicalType?.coding?.find(
    (c) => c.system === LOCATION_PHYSICAL_TYPE_SYSTEM,
  )?.code;
  if (code === PHYSICAL_TYPE_BUILDING) return UNIFY_PROFILES.locationSite;
  if (code === PHYSICAL_TYPE_ROOM) return UNIFY_PROFILES.locationRoom;
  // Unknown/missing physical type: treat as site by default so it is still validated.
  return UNIFY_PROFILES.locationSite;
}

const STATIC_PROFILE_BY_TYPE: Partial<Record<Resource['resourceType'], string>> = {
  Appointment: UNIFY_PROFILES.appointment,
  QuestionnaireResponse: UNIFY_PROFILES.questionnaireResponse,
  ServiceRequest: UNIFY_PROFILES.serviceRequest,
  EpisodeOfCare: UNIFY_PROFILES.episodeOfCare,
  Task: UNIFY_PROFILES.task,
  Patient: UNIFY_PROFILES.patient,
  Practitioner: UNIFY_PROFILES.practitioner,
  RelatedPerson: UNIFY_PROFILES.relatedPerson,
  Coverage: UNIFY_PROFILES.coverage,
  DocumentReference: UNIFY_PROFILES.documentReference,
  Communication: UNIFY_PROFILES.communication,
  CommunicationRequest: UNIFY_PROFILES.communicationRequest,
  HealthcareService: UNIFY_PROFILES.healthcareService,
  Schedule: UNIFY_PROFILES.schedule,
  Encounter: UNIFY_PROFILES.encounter,
  Organization: UNIFY_PROFILES.organization,
  PractitionerRole: UNIFY_PROFILES.practitionerRole,
  CareTeam: UNIFY_PROFILES.careTeam,
  Account: UNIFY_PROFILES.account,
  Observation: UNIFY_PROFILES.observation,
  Device: UNIFY_PROFILES.device,
  Group: UNIFY_PROFILES.group,
  Questionnaire: UNIFY_PROFILES.questionnaire,
  ActivityDefinition: UNIFY_PROFILES.activityDefinition,
  PlanDefinition: UNIFY_PROFILES.planDefinition,
  ChargeItem: UNIFY_PROFILES.chargeItem,
  ChargeItemDefinition: UNIFY_PROFILES.chargeItemDefinition,
  Claim: UNIFY_PROFILES.claim,
  CoverageEligibilityRequest: UNIFY_PROFILES.coverageEligibilityRequest,
  CoverageEligibilityResponse: UNIFY_PROFILES.coverageEligibilityResponse,
  Provenance: UNIFY_PROFILES.provenance,
  CarePlan: UNIFY_PROFILES.carePlan,
  Goal: UNIFY_PROFILES.goal,
};

/**
 * Returns the expected canonical Unify profile URL for a resource, or
 * `undefined` if the resource type is intentionally unprofiled (infra) or not
 * covered by Unify profiles.
 */
export function resolveExpectedProfile(resource: Resource): string | undefined {
  if (resource.resourceType === 'Location') {
    return resolveLocationProfile(resource);
  }
  return STATIC_PROFILE_BY_TYPE[resource.resourceType];
}

/** True when the resource type carries a Unify profile (i.e. is validated). */
export function isProfiledResource(resource: Resource): boolean {
  return resolveExpectedProfile(resource) !== undefined;
}

/** All resource types that carry a Unify profile (Location appears once; subtype resolved per-instance). */
export const PROFILED_RESOURCE_TYPES: readonly string[] = [
  ...Object.keys(STATIC_PROFILE_BY_TYPE),
  'Location',
];
